import DentalCBCTThumbnail from '@/src/assets/samples/Dental-CBCT.jpg';
import { SegmentMask } from '@/src/types/segment';
import type { LayoutConfig } from './utils/layoutParsing';
import type { ViewInfoInit } from './types/views';
import { SampleDataset } from './types';
import { Action } from './constants';

/**
 * These are the initial view IDs.
 *
 * These view IDs get mapped to components in core/viewTypes.ts.
 */
export const InitViewIDs: Record<string, string> = {
  Coronal: 'Coronal',
  Sagittal: 'Sagittal',
  Axial: 'Axial',
  Three: '3D',
  ObliqueCoronal: 'ObliqueCoronal',
  ObliqueSagittal: 'ObliqueSagittal',
  ObliqueAxial: 'ObliqueAxial',
  ObliqueThree: 'Oblique3D',
};

export const getAvailableViews = () => {
  const list: ViewInfoInit[] = [
    {
      name: 'Oblique',
      type: 'Oblique',
      dataID: null,
      options: {},
    },
    {
      name: 'Volume',
      type: '3D',
      dataID: null,
      options: {
        viewDirection: 'Posterior',
        viewUp: 'Superior',
      },
    },
    {
      name: 'Coronal',
      type: '2D',
      dataID: null,
      options: {
        orientation: 'Coronal',
      },
    },
    {
      name: 'Sagittal',
      type: '2D',
      dataID: null,
      options: {
        orientation: 'Sagittal',
      },
    },
    {
      name: 'Axial',
      type: '2D',
      dataID: null,
      options: {
        orientation: 'Axial',
      },
    },
  ];

  const byName = list.reduce(
    (acc, view) => ({ ...acc, [view.name]: view }),
    {} as Record<string, ViewInfoInit>
  );

  return { list, byName };
};

export const DefaultNamedLayouts: Record<string, LayoutConfig> = {
  'Four Up': [
    ['axial', 'coronal'],
    ['sagittal', 'volume'],
  ],
  'Axial Coronal Sagittal': {
    direction: 'row',
    items: [
      'axial',
      {
        direction: 'column',
        items: ['coronal', 'sagittal'],
      },
    ],
  },
  'Axial Only': [['axial']],
  '3D Only': [['volume']],
  Oblique: [['oblique']],
  '3D Primary': {
    direction: 'row',
    items: [
      'volume',
      {
        direction: 'column',
        items: ['axial', 'coronal', 'sagittal'],
      },
    ],
  },
};

export const SAMPLE_DATA: SampleDataset[] = [
  {
    name: 'Dental CBCT with Segmentation',
    filename: 'sample-dental-case.zip',
    description:
      'CBCT scan with segmented mandible, upper skull, mandibular canal, and individual teeth meshes. (144 MB)',
    // Served by Caddy on the viewer VPS from /srv/samples (with CORS), so
    // sample downloads stay same-origin in production and have no
    // third-party bandwidth quotas.
    url: 'https://viewer.pycad.co/samples/sample-dental-case.zip',
    image: DentalCBCTThumbnail,
  },
];

export const TOOL_COLORS = [
  '#58f24c',
  '#8de4d3',
  '#f0a4b1',
  '#a3c9fe',
  '#c8f251',
  '#fea53b',
];

export const STROKE_WIDTH_ANNOTATION_TOOL_DEFAULT = 1;

// Each tool type gets its own default color so annotations are visually
// distinguishable by type at a glance.
export const RULER_LABEL_DEFAULTS = {
  'Label 1': { color: '#00e5ff' }, // cyan
};

export const ANGLE_LABEL_DEFAULTS = {
  'Label 1': { color: '#ff9f43' }, // orange
};

export const RECTANGLE_LABEL_DEFAULTS = {
  'Label 1': { color: '#ff5c8a' }, // pink
};

export const POLYGON_LABEL_DEFAULTS = {
  'Label 1': { color: '#b980ff' }, // purple
};

export const ARROW_LABEL_DEFAULTS = {
  'Label 1': { color: '#ff9900' }, // SmileSuite orange
};

export const CIRCLE_LABEL_DEFAULTS = {
  'Label 1': { color: '#4cd964' }, // green
};

export const FREEHAND_LABEL_DEFAULTS = {
  'Label 1': { color: '#ff5c5c' }, // red
};

export const DEFAULT_PRESET_BY_MODALITY: Record<string, string> = {
  CT: 'CT-AAA',
  MR: 'CT-Coronary-Arteries-2',
  US: 'US-Fetal',
};
export const DEFAULT_PRESET = 'CT-AAA';

export const LAYER_PRESET_BY_MODALITY: Record<string, string> = {
  ...DEFAULT_PRESET_BY_MODALITY,
  PT: '2hot-opaque',
};
export const LAYER_PRESET_DEFAULT = '2hot-opaque';

// Keyboard shortcuts/hotkeys. Can add modifiers: 'Shift+Ctrl+a'
export const ACTION_TO_KEY = {
  windowLevel: 'l',
  pan: 'n',
  zoom: 'z',
  ruler: 'm',
  paint: 'p',
  paintEraser: 'e',
  brushSizeModifier: 'ctrl',
  decreaseBrushSize: '[',
  increaseBrushSize: ']',
  rectangle: 'r',
  crosshairs: 'c',
  temporaryCrosshairs: 'shift-c',
  crop: 'b',
  polygon: 'g',
  angle: 'a',
  arrow: 'k',
  circle: 'o',
  freehand: 'f',
  mergeNewPolygon: 'Shift',
  panoramic: 'shift+p',
  select: 's',

  nextSlice: 'arrowdown',
  previousSlice: 'arrowup',
  grabSlice: 'Alt',

  decrementLabel: 'q',
  incrementLabel: 'w',

  deleteCurrentImage: 'ctrl+.',
  clearScene: 'ctrl+/',

  showKeyboardShortcuts: '?',
} satisfies Record<Action, string>;

export const DEFAULT_SEGMENT_MASKS: SegmentMask[] = [
  {
    value: 1,
    name: 'Segment 1',
    color: [255, 255, 0, 255],
    visible: true,
  },
];

// from https://github.com/InsightSoftwareConsortium/itk-viewer-color-maps/blob/main/src/CategoricalColors.json
export const CATEGORICAL_COLORS = [
  [214, 0, 0],
  [59, 255, 1],
  [0, 0, 172],
  [151, 255, 0],
  [126, 209, 107],
  [79, 255, 165],
  [87, 59, 0],
  [86, 89, 0],
  [221, 0, 253],
  [161, 117, 105],
  [182, 255, 149],
  [119, 191, 3],
  [100, 84, 116],
  [0, 0, 7],
  [216, 253, 244],
  [0, 75, 0],
  [121, 0, 255],
  [102, 237, 184],
  [93, 126, 102],
  [228, 255, 235],
  [119, 165, 123],
  [89, 0, 163],
  [198, 0, 158],
  [0, 156, 59],
  [202, 195, 0],
  [130, 151, 0],
  [137, 130, 135],
  [93, 54, 59],
  [0, 0, 253],
  [255, 189, 230],
  [219, 109, 1],
  [184, 181, 228],
  [255, 47, 82],
  [195, 102, 144],
  [98, 31, 196],
  [114, 3, 130],
  [105, 230, 128],
  [38, 144, 109],
  [255, 77, 51],
  [133, 163, 1],
  [3, 202, 193],
  [196, 196, 86],
  [117, 87, 61],
  [103, 66, 0],
  [212, 218, 223],
  [249, 255, 0],
  [103, 175, 195],
  [0, 225, 205],
  [218, 149, 255],
  [3, 253, 145],
  [130, 160, 0],
  [86, 154, 84],
  [140, 142, 54],
  [38, 151, 165],
  [142, 140, 94],
  [70, 0, 200],
  [249, 174, 109],
  [110, 207, 167],
  [255, 140, 140],
  [177, 119, 54],
  [255, 160, 121],
  [0, 31, 255],
  [68, 94, 17],
  [103, 151, 147],
  [94, 147, 75],
  [116, 82, 145],
  [170, 112, 49],
  [207, 253, 0],
  [107, 96, 52],
  [144, 212, 47],
  [212, 124, 80],
  [161, 77, 35],
  [124, 89, 0],
  [205, 68, 130],
  [207, 77, 253],
  [137, 0, 61],
  [82, 91, 0],
  [156, 170, 130],
  [128, 112, 142],
  [100, 253, 195],
  [137, 205, 40],
  [255, 154, 181],
  [93, 186, 33],
  [1, 0, 142],
  [98, 128, 35],
  [135, 191, 151],
  [212, 205, 126],
  [209, 182, 91],
  [0, 110, 153],
  [68, 175, 198],
  [242, 255, 209],
  [235, 1, 205],
  [188, 68, 0],
  [121, 156, 126],
  [112, 70, 147],
  [186, 0, 84],
  [172, 147, 235],
  [163, 22, 94],
  [128, 0, 75],
  [124, 184, 211],
  [42, 0, 56],
  [100, 184, 0],
  [255, 128, 61],
  [209, 232, 128],
  [89, 33, 52],
  [161, 93, 110],
  [181, 175, 158],
  [70, 51, 124],
  [193, 65, 0],
  [232, 61, 107],
  [232, 117, 188],
  [165, 196, 168],
  [84, 110, 216],
  [56, 251, 124],
  [75, 100, 73],
  [195, 235, 121],
  [54, 75, 142],
  [70, 135, 255],
  [0, 195, 233],
  [212, 255, 188],
  [70, 72, 0],
  [198, 255, 144],
  [233, 79, 105],
  [230, 93, 177],
  [144, 175, 87],
  [42, 175, 93],
  [133, 109, 225],
  [110, 114, 228],
  [226, 184, 182],
  [56, 45, 0],
  [126, 163, 172],
  [47, 168, 186],
  [105, 181, 130],
  [209, 144, 175],
  [70, 7, 94],
  [0, 151, 137],
  [15, 1, 91],
  [128, 47, 87],
  [228, 100, 59],
  [63, 40, 114],
  [188, 75, 82],
  [200, 121, 221],
  [49, 144, 200],
  [242, 5, 170],
  [167, 107, 154],
  [175, 0, 96],
  [98, 242, 221],
  [119, 68, 1],
  [36, 65, 103],
  [202, 121, 158],
  [12, 232, 161],
  [247, 219, 131],
  [117, 142, 109],
  [226, 65, 47],
  [73, 107, 121],
  [133, 255, 207],
  [75, 93, 198],
  [179, 145, 255],
  [237, 214, 239],
  [191, 96, 38],
  [163, 181, 188],
  [0, 135, 110],
  [255, 47, 161],
  [232, 175, 51],
  [75, 184, 140],
  [107, 135, 82],
  [147, 209, 26],
  [253, 161, 59],
  [163, 80, 168],
  [0, 151, 137],
  [123, 89, 87],
  [249, 142, 138],
  [214, 124, 112],
  [1, 24, 89],
  [22, 38, 255],
  [216, 86, 247],
  [253, 121, 149],
  [177, 167, 212],
  [207, 221, 0],
  [175, 121, 70],
  [218, 255, 230],
  [5, 177, 242],
  [255, 163, 228],
  [137, 19, 35],
  [103, 130, 232],
  [112, 216, 170],
  [223, 186, 212],
  [82, 105, 117],
  [154, 151, 51],
  [228, 114, 126],
  [89, 38, 119],
  [105, 47, 61],
] as const;
